import { Controller } from "@hotwired/stimulus"

// "Chat with your plans / specs" — the AI chat panel on the Drawings and
// Specifications pages (shared/_docs_ai_chat partial), opened from the
// toolbar's "Ask AI" button. Lives on the page's root container alongside
// the page controller; on large screens opening the panel pads the container
// so the table slides left and stays fully visible next to the chat.
// Answers come from the `ask` endpoint (Gemini, grounded in the document
// list + indexed page text).
export default class extends Controller {
  static targets = ["panel", "messages", "input", "clearConfirm"]
  static values = { askUrl: String, clearUrl: String, crossAskUrl: String, crossLabel: String,
                    webLookupUrl: String }

  // Panel is 400px wide and viewport-anchored; the layout's <main> already
  // adds 24px right padding, so 388 + 24 leaves a slim ~12px gap to the table.
  PANEL_GAP = "388px"

  connect() {
    // Mouse-wheel anywhere over the panel scrolls the conversation — never
    // the table behind it (no scroll chaining at the list's top/bottom, and
    // the header/input areas scroll the chat too).
    this._onPanelWheel = (e) => {
      e.preventDefault()
      const unit = e.deltaMode === 1 ? 16 : 1 // line-mode wheels (Firefox)
      this.messagesTarget.scrollTop += e.deltaY * unit
    }
    if (this.hasPanelTarget) this.panelTarget.addEventListener("wheel", this._onPanelWheel, { passive: false })
  }

  disconnect() {
    if (this.hasPanelTarget) this.panelTarget.removeEventListener("wheel", this._onPanelWheel)
  }

  open(e) {
    if (e) e.preventDefault()
    this.panelTarget.classList.remove("hidden")
    this.panelTarget.classList.add("flex")
    // Push the page content left (desktop only — on small screens the panel
    // overlays full-width instead, there's no room to split).
    if (window.matchMedia("(min-width: 1024px)").matches) {
      this.element.style.transition = "padding-right 250ms ease"
      this.element.style.paddingRight = this.PANEL_GAP
    }
    // Saved conversations render server-side — land on the latest message.
    this._scroll()
    this.inputTarget.focus()
  }

  close(e) {
    if (e) e.preventDefault()
    this.panelTarget.classList.add("hidden")
    this.panelTarget.classList.remove("flex")
    this.element.style.paddingRight = ""
  }

  // Suggestion chip → fill the input and send.
  suggest(e) {
    if (e) e.preventDefault()
    this.inputTarget.value = e.currentTarget.dataset.q || ""
    this.send()
  }

  send(e) {
    if (e) e.preventDefault()
    const question = this.inputTarget.value.trim()
    if (!question || this._busy) return

    this._appendUser(question)
    this.inputTarget.value = ""
    this._busy = true
    const thinking = this._appendThinking()

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.askUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token || "", "Accept": "application/json" },
      body: JSON.stringify({ question })
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((data) => {
        thinking.remove()
        this._appendAi(data.answer || "Sorry, I couldn't answer that.")
        // External codes/standards the answer mentioned — clearly-marked links.
        if (Array.isArray(data.code_refs) && data.code_refs.length) this._appendCodeRefs(data.code_refs)
        // The answer cited documents — offer a "Show me" that filters the
        // list to exactly those sheets/sections (like the search bar).
        if (Array.isArray(data.sources) && data.sources.length) this._appendShowMe(data.sources)
        // The answer points at the other document set — offer a one-tap
        // cross-reference that runs the suggested question over there.
        if (data.cross_query && this.hasCrossAskUrlValue) this._appendCrossRef(data.cross_query)
        // The question asks what an external code requires — offer a live
        // web search (results render in the blue "from the web" style).
        if (data.web_query && this.hasWebLookupUrlValue) this._appendWebLookup(data.web_query)
      })
      .catch(() => { thinking.remove(); this._appendAi("Sorry — I couldn't reach the AI just now. Please try again.") })
      .finally(() => { this._busy = false; this.inputTarget.focus() })
  }

  // ── External code references — collapsed behind an "External references (N)"
  // link; the amber box (clearly NOT from the project documents) expands on
  // click. Same flow for live answers and saved ones (toggleCodeRefs). ──
  _appendCodeRefs(refs) {
    const wrap = document.createElement("div")
    wrap.className = "pl-8 space-y-1"
    wrap.appendChild(this._codeRefsToggle(refs))
    this.messagesTarget.appendChild(wrap)
    this._scroll()
  }

  // Saved answers render this toggle server-side with the refs embedded.
  toggleCodeRefs(e) {
    if (e) e.preventDefault()
    let refs = []
    try { refs = JSON.parse(e.currentTarget.dataset.codeRefs || "[]") } catch (_) { /* stale attr */ }
    this._toggleCodeRefsBox(e.currentTarget, refs)
  }

  _codeRefsToggle(refs) {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "inline-flex items-center gap-1 text-[13px] text-amber-700 font-medium hover:underline cursor-pointer"
    btn.textContent = `↗ External references (${refs.length})`
    btn.addEventListener("click", () => this._toggleCodeRefsBox(btn, refs))
    return btn
  }

  // Build the amber box on first open (lazily), then show/hide on each click.
  _toggleCodeRefsBox(btn, refs) {
    let box = btn.parentElement.querySelector("[data-code-refs-box]")
    if (!box) {
      box = this._buildCodeRefsBox(refs)
      btn.insertAdjacentElement("afterend", box)
      this._scroll()
      return
    }
    box.classList.toggle("hidden")
    if (!box.classList.contains("hidden")) this._scroll()
  }

  _buildCodeRefsBox(refs) {
    const box = document.createElement("div")
    box.setAttribute("data-code-refs-box", "")
    box.className = "rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 space-y-1"

    const head = document.createElement("div")
    head.className = "text-[13px] font-medium text-amber-800"
    head.textContent = "↗ External code references — not part of your project documents"
    box.appendChild(head)

    let anyRelevance = false
    refs.slice(0, 5).forEach((ref) => {
      if (!ref || !ref.name) return
      const line = document.createElement("div")
      line.className = "text-[13px] text-gray-700"
      const url = typeof ref.url === "string" && ref.url.startsWith("http") ? ref.url : null
      if (url) {
        const a = document.createElement("a")
        a.href = url
        a.target = "_blank"
        a.rel = "noopener noreferrer"
        a.className = "text-amber-800 font-medium underline hover:text-amber-900"
        a.textContent = `${ref.name} ↗`
        line.appendChild(a)
      } else {
        const strong = document.createElement("span")
        strong.className = "text-amber-800 font-medium"
        strong.textContent = ref.name
        line.appendChild(strong)
      }
      if (ref.note) line.appendChild(document.createTextNode(` — ${ref.note}`))
      box.appendChild(line)

      // The part of the code the AI believes applies here, in its own words.
      if (ref.relevance) {
        anyRelevance = true
        const rel = document.createElement("div")
        rel.className = "text-[13px] text-gray-600 pl-3 border-l-2 border-amber-200 mb-1"
        const tag = document.createElement("span")
        tag.className = "font-medium text-amber-800"
        tag.textContent = "Most relevant here: "
        rel.appendChild(tag)
        rel.appendChild(document.createTextNode(ref.relevance))
        box.appendChild(rel)
      }
    })

    if (anyRelevance) {
      const foot = document.createElement("div")
      foot.className = "text-[12px] text-amber-700/80 italic"
      foot.textContent = "AI summary from general knowledge — verify against the official text."
      box.appendChild(foot)
    }
    return box
  }

  // ── "Show me" — filter the list to the answer's source documents ──

  // Saved answers render their link server-side with the sources embedded.
  showSources(e) {
    if (e) e.preventDefault()
    let sources = []
    try { sources = JSON.parse(e.currentTarget.dataset.sources || "[]") } catch (_) { /* stale attr */ }
    this._emitSources(sources)
  }

  _appendShowMe(sources) {
    const row = document.createElement("div")
    row.className = "flex pl-8"
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "inline-flex items-center gap-1 text-[13px] text-purple-700 font-medium hover:underline cursor-pointer"
    btn.textContent = "⌕ Show me in the list →"
    btn.addEventListener("click", () => this._emitSources(sources))
    row.appendChild(btn)
    this.messagesTarget.appendChild(row)
    this._scroll()
  }

  // The Drawings/Specs list controller (same page) listens for this event and
  // filters the table to the cited documents.
  _emitSources(sources) {
    if (!Array.isArray(sources) || !sources.length) return
    window.dispatchEvent(new CustomEvent("docs-ai:show-sources", { detail: { sources } }))
  }

  // ── "Check the web" — live web search for external code content ──
  // Results render in a BLUE block (vs amber for code links, gray for the
  // project documents) so it's obvious the info came from the internet.
  _appendWebLookup(query) {
    const row = document.createElement("div")
    row.className = "flex pl-8"
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-blue-200 bg-blue-50 text-blue-700 text-[13px] hover:bg-blue-100 cursor-pointer"
    btn.textContent = "🌐 Check the web for this"
    btn.addEventListener("click", () => this._runWebLookup(btn, row, query))
    row.appendChild(btn)
    this.messagesTarget.appendChild(row)
    this._scroll()
  }

  _runWebLookup(btn, row, query) {
    if (this._busy) return
    this._busy = true
    btn.disabled = true
    btn.style.opacity = "0.6"
    btn.textContent = "Searching the web…"

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.webLookupUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token || "", "Accept": "application/json" },
      body: JSON.stringify({ query })
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((data) => {
        row.remove()
        this._appendWebResult(data.answer || "I couldn't find a reliable answer on the web.", data.links)
      })
      .catch(() => {
        btn.disabled = false
        btn.style.opacity = ""
        btn.textContent = "🌐 Check the web for this"
      })
      .finally(() => { this._busy = false })
  }

  _appendWebResult(answer, links) {
    const row = document.createElement("div")
    row.className = "flex pl-8"
    const box = document.createElement("div")
    box.className = "flex-1 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 space-y-1"

    const head = document.createElement("div")
    head.className = "text-[13px] font-medium text-blue-800"
    head.textContent = "🌐 From the web — verify with the official source"
    box.appendChild(head)

    const body = document.createElement("div")
    body.className = "text-[13px] text-gray-700 whitespace-pre-wrap"
    body.textContent = answer
    box.appendChild(body)

    if (Array.isArray(links) && links.length) {
      const list = document.createElement("div")
      list.className = "flex flex-wrap gap-x-3 gap-y-0.5 pt-0.5"
      links.slice(0, 4).forEach((l) => {
        if (!l || typeof l.url !== "string" || !l.url.startsWith("http")) return
        const a = document.createElement("a")
        a.href = l.url
        a.target = "_blank"
        a.rel = "noopener noreferrer"
        a.className = "text-[13px] text-blue-700 underline hover:text-blue-900 truncate max-w-full"
        a.textContent = `${(l.title || l.url).slice(0, 60)} ↗`
        list.appendChild(a)
      })
      box.appendChild(list)
    }

    row.appendChild(box)
    this.messagesTarget.appendChild(row)
    this._scroll()
  }

  // ── Cross-reference with the other document set ──
  _appendCrossRef(query) {
    const label = this.crossLabelValue || "documents"
    const row = document.createElement("div")
    row.className = "flex pl-8"
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-purple-200 bg-purple-50 text-purple-700 text-[13px] hover:bg-purple-100 cursor-pointer"
    btn.textContent = `✦ Cross-reference with ${label}`
    btn.addEventListener("click", () => this._runCrossRef(btn, row, query))
    row.appendChild(btn)
    this.messagesTarget.appendChild(row)
    this._scroll()
  }

  _runCrossRef(btn, row, query) {
    if (this._busy) return
    this._busy = true
    const label = this.crossLabelValue || "documents"
    btn.disabled = true
    btn.style.opacity = "0.6"
    btn.textContent = `Checking the ${label}…`

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.crossAskUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token || "", "Accept": "application/json" },
      body: JSON.stringify({ question: query, source: "cross_reference" })
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((data) => {
        row.remove()
        this._appendAi(`From the ${label}: ${data.answer || "I couldn't find anything on that."}`)
        if (Array.isArray(data.code_refs) && data.code_refs.length) this._appendCodeRefs(data.code_refs)
      })
      .catch(() => {
        btn.disabled = false
        btn.style.opacity = ""
        btn.textContent = `✦ Cross-reference with ${label}`
      })
      .finally(() => { this._busy = false })
  }

  _appendUser(text) {
    const row = document.createElement("div")
    row.className = "flex gap-2 justify-end"
    const bubble = document.createElement("div")
    // Light mode keeps purple-600 + white (unchanged). A dark-mode-only CSS rule
    // on .docs-user-bubble pins a readable deep-purple + white, because in dark
    // mode bg-purple-600 lightens while text-white stays white (unreadable).
    bubble.className = "docs-user-bubble bg-purple-600 text-white rounded-lg rounded-tr-none px-3 py-2 text-[13px] max-w-[270px] whitespace-pre-wrap"
    bubble.textContent = text
    row.appendChild(bubble)
    this.messagesTarget.appendChild(row)
    this._scroll()
  }

  _appendAi(text) {
    const row = document.createElement("div")
    row.className = "flex gap-2"
    const avatar = document.createElement("div")
    avatar.className = "w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-[11px] shrink-0"
    avatar.textContent = "AI"
    const bubble = document.createElement("div")
    bubble.className = "bg-gray-100 rounded-lg rounded-tl-none px-3 py-2 text-[13px] text-gray-700 whitespace-pre-wrap"
    bubble.textContent = text
    row.appendChild(avatar); row.appendChild(bubble)
    this.messagesTarget.appendChild(row)
    this._scroll()
  }

  _appendThinking() {
    const row = document.createElement("div")
    row.className = "flex gap-2"
    row.innerHTML = `<div class="w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-[11px] shrink-0">AI</div>
      <div class="bg-gray-100 rounded-lg rounded-tl-none px-3 py-2 text-[13px] text-gray-400 italic">Thinking…</div>`
    this.messagesTarget.appendChild(row)
    this._scroll()
    return row
  }

  _scroll() { this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight }

  // ── Clear chat (saved history) ─────────────────────────────────────
  // Trash icon shows an inline confirm strip; "Clear" deletes the saved
  // conversation server-side and resets the panel to its greeting.
  askClear(e) {
    if (e) e.preventDefault()
    if (this.hasClearConfirmTarget) this.clearConfirmTarget.classList.remove("hidden")
  }

  cancelClear(e) {
    if (e) e.preventDefault()
    if (this.hasClearConfirmTarget) this.clearConfirmTarget.classList.add("hidden")
  }

  clear(e) {
    if (e) e.preventDefault()
    if (!this.clearUrlValue || this._busy) return
    this._busy = true

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.clearUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": token || "", "Accept": "application/json" }
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then(() => {
        // Keep only the greeting (first bubble); drop saved + live messages.
        const rows = [...this.messagesTarget.children]
        rows.slice(1).forEach((n) => n.remove())
      })
      .catch(() => {})
      .finally(() => {
        this._busy = false
        this.cancelClear()
        this.inputTarget.focus()
      })
  }
}
